import React, { useState } from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  Divider,
  Link,
} from '@mui/material';
import { Mail, Phone, MapPin, Send } from 'lucide-react';

const InfoPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    // Opens the user's mail client with pre-filled fields as a simple fallback
    const subject = encodeURIComponent(`Yhteydenotto: ${name}`);
    const body = encodeURIComponent(`Nimi: ${name}\nSähköposti: ${email}\n\n${message}`);
    window.location.href = `mailto:info@pakettihuolto.fi?subject=${subject}&body=${body}`;
    setLoading(false);
    setSubmitted(true);
  };

  const contactItems = [
    {
      icon: <Mail size={20} />,
      label: 'Sähköposti',
      value: 'info@pakettihuolto.fi',
      href: 'mailto:info@pakettihuolto.fi',
    },
    {
      icon: <Phone size={20} />,
      label: 'Puhelin',
      value: '010 200 5000',
      href: 'tel:+358102005000',
    },
    {
      icon: <MapPin size={20} />,
      label: 'Osoite',
      value: 'Helsinki, Suomi',
      href: null,
    },
  ];

  return (
    <Box
      sx={{
        bgcolor: 'background.default',
        minHeight: '100vh',
        pt: { xs: 10, sm: 12 },
        pb: 8,
      }}
    >
      <Container maxWidth="md">
        {/* Page heading */}
        <Box sx={{ mb: 6, textAlign: 'center' }}>
          <Typography variant="h4" fontWeight={700} color="text.primary" gutterBottom>
            Ota yhteyttä
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 480, mx: 'auto' }}>
            Autamme mielellämme kaikissa paketteihin liittyvissä kysymyksissä.
            Vastaamme yleensä saman päivän aikana.
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1.6fr' },
            gap: 3,
            alignItems: 'start',
          }}
        >
          {/* ── Contact info card ── */}
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="subtitle1" fontWeight={700} color="text.primary" gutterBottom>
                Yhteystiedot
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Voit ottaa meihin yhteyttä alla olevien tietojen kautta tai käyttää oikealla olevaa lomaketta.
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {contactItems.map((item) => (
                  <Box key={item.label} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <Box
                      sx={{
                        mt: 0.25,
                        color: 'primary.main',
                        flexShrink: 0,
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {item.label}
                      </Typography>
                      {item.href ? (
                        <Link
                          href={item.href}
                          variant="body2"
                          fontWeight={500}
                          color="text.primary"
                          underline="hover"
                        >
                          {item.value}
                        </Link>
                      ) : (
                        <Typography variant="body2" fontWeight={500} color="text.primary">
                          {item.value}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>

              <Divider sx={{ my: 3 }} />

              <Typography variant="caption" color="text.secondary">
                Palvelemme arkisin klo 8–17.
              </Typography>
            </CardContent>
          </Card>

          {/* ── Contact form card ── */}
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="subtitle1" fontWeight={700} color="text.primary" gutterBottom>
                Lähetä viesti
              </Typography>

              {submitted ? (
                <Alert severity="success" sx={{ mt: 2 }}>
                  Kiitos viestistäsi! Sähköpostiohjelmasi avautuu viestin lähettämistä varten.
                </Alert>
              ) : (
                <form onSubmit={handleSubmit}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <TextField
                        fullWidth
                        label="Nimesi"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                      />
                      <TextField
                        fullWidth
                        label="Sähköpostisi"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </Box>

                    <TextField
                      fullWidth
                      label="Viesti"
                      multiline
                      rows={5}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      required
                      placeholder="Kirjoita viestisi tähän..."
                    />

                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      disabled={loading}
                      endIcon={<Send size={16} />}
                      sx={{ alignSelf: 'flex-end' }}
                    >
                      {loading ? 'Lähetetään...' : 'Lähetä viesti'}
                    </Button>
                  </Box>
                </form>
              )}
            </CardContent>
          </Card>
        </Box>
      </Container>
    </Box>
  );
};

export default InfoPage;